import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';

/**
 * Phishing tab — investigates phish/spam/malware emails that bypassed
 * Defender for Office 365 and were delivered to users.
 *
 * Layout:
 *   1. Filter bar (lookback, recipient/sender/subject substring filters)
 *   2. Bypass-reason summary tiles (org allow / user override / auth / filter)
 *   3. Sortable table of delivered phish — click a row to open the drawer
 *   4. Right-side drawer with full per-email telemetry + AI investigation
 *
 * The "row click → drawer" pattern matches EntityInvestigationPanel; the
 * drawer fetches the full detail bundle on first open and caches it by
 * NetworkMessageId so re-opening the same email is instant.
 */

interface DeliveredResponse {
  customerId: string;
  days: number;
  kql: string;
  status: 'ok' | 'missing' | 'error';
  error?: string;
  message?: string;
  columns: string[];
  rows: unknown[][];
  rowCount?: number;
  truncated?: boolean;
}

interface BypassResponse {
  customerId: string;
  days: number;
  kql: string;
  status: 'ok' | 'missing' | 'error';
  error?: string;
  columns: string[];
  rows: unknown[][];
}

interface SenderHuntResponse {
  customerId: string;
  sender: string;
  days: number;
  kql: string;
  status: 'ok' | 'missing' | 'error';
  error?: string;
  message?: string;
  columns: string[];
  rows: unknown[][];
  rowCount?: number;
  truncated?: boolean;
}

interface DetailSection {
  id: 'email' | 'urls' | 'attachments' | 'postDelivery' | 'urlClicks' | 'recipients' | 'campaign';
  title: string;
  status: 'ok' | 'missing' | 'error';
  error?: string;
  kql: string;
  columns: string[];
  rows: unknown[][];
}

interface LiveWatchSection {
  status: 'ok' | 'missing' | 'error';
  error?: string;
  message?: string;
  kql: string;
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
}
interface LiveWatchResponse {
  customerId: string;
  sinceMinutes: number;
  queriedAt: string;
  newMail: LiveWatchSection;
  newClicks: LiveWatchSection;
}

// ─── IOC reputation batch ────────────────────────────────────────────────────
type Verdict = 'malicious' | 'suspicious' | 'clean' | 'unknown';
interface ProviderResult {
  provider: string;
  ok: boolean;
  verdict: Verdict;
  summary?: string;
  link?: string;
  error?: string;
}
interface IocReport {
  ioc: string;
  type: 'ip' | 'domain' | 'url' | 'hash' | 'unknown';
  overallVerdict: Verdict;
  malicious: number;
  suspicious: number;
  total: number;
  results: ProviderResult[];
}
interface BatchReportEntry {
  ioc: string;
  ok: boolean;
  report?: IocReport;
  error?: string;
}

interface DetailResponse {
  customerId: string;
  networkMessageId: string;
  days: number;
  sections: DetailSection[];
}

interface AiAnalysis {
  bypassReason: string;
  severity: 'Low' | 'Medium' | 'High' | 'Critical' | 'Unknown';
  attackerObjective: string;
  iocs: { type: string; value: string; note?: string }[];
  recommendedActions: string[];
  filterTuning: string[];
  narrative: string;
}

interface AiResponse {
  customerId: string;
  networkMessageId: string;
  factsSummary: { urls: number; attachments: number; postDeliveryEvents: number; urlClicks: number };
  analysis: AiAnalysis;
}

/** Sender-level AI investigation response (from /phishing/sender-ai-investigate).
 *  Same `analysis` shape as the per-email AiResponse so we can reuse the
 *  AiAnalysisCard renderer; only the facts-summary keys differ. */
interface SenderAiResponse {
  customerId: string;
  sender: string;
  days: number;
  factsSummary: { recipients: number; messages: number; distinctSubjects: number; senderIPs: number };
  /** Per-section status of the live KQL grounding the model was given.
   *  Surfaced under the report so the analyst sees exactly what backed the verdict. */
  grounding?: { section: string; status: 'ok' | 'empty' | 'missing' | 'error'; rowCount: number }[];
  analysis: AiAnalysis;
}

const SEVERITY_COLOR: Record<AiAnalysis['severity'], string> = {
  Critical: 'bg-red-500/30 text-red-700 dark:text-red-200 border-red-500/50',
  High:     'bg-orange-500/30 text-orange-700 dark:text-orange-200 border-orange-500/50',
  Medium:   'bg-amber-500/30 text-amber-700 dark:text-amber-200 border-amber-500/50',
  Low:      'bg-green-500/30 text-green-700 dark:text-green-200 border-green-500/50',
  Unknown:  'bg-slate-500/30 text-slate-700 dark:text-slate-200 border-slate-500/50',
};

/** Convert columnar KQL response into row-objects keyed by column name. */
function toObjects(columns: string[], rows: unknown[][]): Record<string, unknown>[] {
  return rows.map((r) => {
    const o: Record<string, unknown> = {};
    columns.forEach((c, i) => { o[c] = r[i]; });
    return o;
  });
}

/** Small helper — render unknown cell values defensively. */
function fmt(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Extract a hostname (or IP) from a URL string. Returns '' if the input
 *  cannot be confidently classified as a hostname/IP — we'd rather skip
 *  enrichment than push raw, malformed strings into the IOC reputation API. */
const HOSTNAME_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;
const IPV4_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/;
function hostnameOf(url: string): string {
  if (!url) return '';
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h || '';
  } catch {
    const trimmed = url.trim().toLowerCase();
    if (HOSTNAME_RE.test(trimmed) || IPV4_RE.test(trimmed)) return trimmed;
    return '';
  }
}

/** Colour-grade Defender DeliveryAction values by analyst impact:
 *   - red    : message landed in the inbox (worst case)
 *   - amber  : message was junked / replaced (partial mitigation)
 *   - green  : message was quarantined / blocked (filter caught it)
 *   - slate  : unknown / blank (we don't fabricate a verdict)
 *
 * The right-hand keys cover the variants Defender emits across DeliveryAction
 * and LatestDeliveryLocation columns, which use slightly different vocab. */
function deliveryPillClass(action: string): string {
  const a = action.toLowerCase();
  if (/(deliveredasspam|junked|junkfolder|replaced)/.test(a))     return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40';
  if (/(quarantine|blocked|dropped|filtered|onpremfailure)/.test(a)) return 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/40';
  if (/(deliver|inbox)/.test(a))                                  return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
  return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40';
}

/** Colour-grade Defender ThreatTypes values: phish/malware are loud, spam is
 *  amber, anything we don't recognise stays neutral. */
function threatPillClass(threat: string): string {
  const t = threat.toLowerCase();
  if (/(phish|malware)/.test(t)) return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
  if (/spam/.test(t))            return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40';
  return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40';
}

/** Tailwind classes per IOC verdict — kept inline so we don't have to import the
 *  IocReputationCard's internal styling map. */
function verdictPill(v: Verdict): string {
  switch (v) {
    case 'malicious':  return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
    case 'suspicious': return 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40';
    case 'clean':      return 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/40';
    default:           return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40';
  }
}

export function PhishingTab() {
  const { activeCustomerId } = useCustomers();
  const { canWrite } = useAuth();
  // Server-side `requireTabWrite('phishing')` is the source of truth on
  // /phishing/sender-hunt and /phishing/ai-investigate; this just keeps the
  // buttons inert when the matrix denies write so users don't get 403s.
  // Matrix-authoritative for every non-admin role (including customer_viewer)
  // — a portal user whose admin granted phishing.write can investigate.
  const readOnly = !canWrite('phishing', activeCustomerId);

  // Filter form state — debounced to a manual "Run" so we don't hammer
  // Log Analytics on every keystroke.
  const [days, setDays] = useState(7);
  const [recipient, setRecipient] = useState('');
  const [sender, setSender] = useState('');
  const [subject, setSubject] = useState('');

  const [delivered, setDelivered] = useState<DeliveredResponse | null>(null);
  const [bypass, setBypass] = useState<BypassResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openNmid, setOpenNmid] = useState<string | null>(null);

  const noCustomer = activeCustomerId === 'default';

  const run = useCallback(async () => {
    if (noCustomer) {
      setError('Select a customer to run a phishing hunt.');
      return;
    }
    setLoading(true);
    setError(null);
    setDelivered(null);
    setBypass(null);
    try {
      const [d, b] = await Promise.all([
        apiFetch<DeliveredResponse>('/phishing/delivered', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: activeCustomerId,
            days,
            recipient: recipient.trim() || undefined,
            sender: sender.trim() || undefined,
            subject: subject.trim() || undefined,
          }),
        }),
        apiFetch<BypassResponse>('/phishing/bypass-analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: activeCustomerId, days }),
        }),
      ]);
      setDelivered(d);
      setBypass(b);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.circuitOpen ? `Service cooling down: ${e.message}` : e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load phishing data');
      }
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId, days, recipient, sender, subject, noCustomer]);

  // Re-run when the active customer changes (clearing prior results so the
  // analyst doesn't see another tenant's data flash on screen).
  useEffect(() => {
    setDelivered(null);
    setBypass(null);
    setOpenNmid(null);
  }, [activeCustomerId]);

  // Header summary numbers derived from the bypass response (one render
  // pass — keeps the JSX below clean).
  const bypassSummary = useMemo(() => {
    if (!bypass || bypass.status !== 'ok') return null;
    const objs = toObjects(bypass.columns, bypass.rows);
    const totals = new Map<string, number>();
    let total = 0;
    for (const r of objs) {
      const reason = String(r['BypassReason'] ?? 'Unknown');
      const count = Number(r['Count'] ?? 0);
      totals.set(reason, (totals.get(reason) ?? 0) + count);
      total += count;
    }
    return { total, byReason: Array.from(totals.entries()).sort((a, b) => b[1] - a[1]) };
  }, [bypass]);

  return (
    <div className="space-y-4 slide-in">
      {/* ── Live Watch: newly-arrived suspicious mail + Safe Links clicks ── */}
      <LiveWatchCard customerId={activeCustomerId} noCustomer={noCustomer} />

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div className="bg-card rounded-xl shadow-md p-4 border border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-bold text-foreground">Phishing: Delivered to Inbox</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Phish, malware, and high-confidence-spam emails that landed in user mailboxes despite Defender for Office 365.
            </p>
          </div>
          <button
            onClick={run}
            disabled={loading || noCustomer}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Running…' : 'Run hunt'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Field label="Lookback (days)">
            <input
              type="number" min={1} max={90} value={days}
              onChange={(e) => setDays(Math.min(90, Math.max(1, Number(e.target.value) || 7)))}
              className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
            />
          </Field>
          <Field label="Recipient contains">
            <input
              value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder="user@…"
              className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
            />
          </Field>
          <Field label="Sender contains">
            <input
              value={sender} onChange={(e) => setSender(e.target.value)}
              placeholder="@example.com"
              className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
            />
          </Field>
          <Field label="Subject contains">
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="invoice"
              className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
            />
          </Field>
          <Field label="">
            <div className="text-[11px] text-muted-foreground pt-1.5">
              {noCustomer
                ? 'Select a customer above.'
                : delivered?.status === 'ok'
                  ? `${delivered.rowCount ?? 0} rows${delivered.truncated ? ' (capped)' : ''}`
                  : ' '}
            </div>
          </Field>
        </div>

        {error && <div className="mt-3 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}
        {delivered?.status === 'missing' && (
          <div className="mt-3 px-3 py-2 bg-amber-500/20 text-amber-700 dark:text-amber-200 text-xs rounded-lg">
            {delivered.message ?? 'EmailEvents table is not available for this customer.'}
          </div>
        )}
      </div>

      {/* ── Bypass-reason summary ──────────────────────────────────────── */}
      {bypassSummary && bypassSummary.total > 0 && (
        <div className="bg-card rounded-xl shadow-md p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-foreground">Bypass analysis</h3>
            <span className="text-[11px] text-muted-foreground">{bypassSummary.total} bypassed in {days}d</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {bypassSummary.byReason.map(([reason, count]) => (
              <div key={reason} className="rounded-lg border border-border bg-background/40 p-3">
                <div className="text-2xl font-bold text-foreground">{count}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sender Hunt: who-received-from-this-sender lookup ─────────── */}
      <SenderHuntCard
        customerId={activeCustomerId}
        readOnly={readOnly}
        noCustomer={noCustomer}
        defaultDays={days}
      />

      {/* ── Delivered-phish table ──────────────────────────────────────── */}
      {delivered?.status === 'ok' && (
        <DeliveredTable response={delivered} onOpen={(nmid) => setOpenNmid(nmid)} />
      )}

      {/* ── Detail drawer ─────────────────────────────────────────────── */}
      {openNmid && (
        <EmailDetailDrawer
          customerId={activeCustomerId}
          networkMessageId={openNmid}
          onClose={() => setOpenNmid(null)}
        />
      )}
    </div>
  );
}

/**
 * Live Watch — a polling view of newly-arrived suspicious mail and Safe Links
 * click events. Self-contained (own state, own polling loop, own visibility
 * pause). Calls `POST /phishing/live-watch` which returns two independent
 * sections so a missing UrlClickEvents table doesn't hide newly-arrived phish
 * (and vice versa).
 *
 * Polling rules:
 *   - Default ON, 60s interval.
 *   - Pauses when the document is hidden (visibilitychange) so a backgrounded
 *     tab doesn't keep hitting Log Analytics.
 *   - Window selector: 5m / 15m / 60m (server clamps 1–360).
 *   - Tracks per-poll delta vs the previous snapshot's row count so the
 *     analyst can see "+N new since last refresh".
 */
const LIVE_WATCH_INTERVAL_MS = 120_000;
function LiveWatchCard({ customerId, noCustomer }: { customerId: string; noCustomer: boolean }) {
  const [sinceMinutes, setSinceMinutes] = useState(15);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [data, setData] = useState<LiveWatchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prevCounts, setPrevCounts] = useState<{ mail: number; clicks: number } | null>(null);

  // Generation token: incremented on every customer/window change AND on every
  // new poll start. After a fetch completes, we drop the result if our gen no
  // longer matches the current one — this kills cross-tenant/window flashes
  // from racy in-flight requests. `inFlight` is a hard guard against
  // overlapping fetches when interval + visibility + manual refresh collide.
  const genRef = useRef(0);
  const inFlightRef = useRef(false);

  const poll = useCallback(async () => {
    if (noCustomer) return;
    if (inFlightRef.current) return;            // single-flight: drop overlap
    inFlightRef.current = true;
    const myGen = ++genRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<LiveWatchResponse>('/phishing/live-watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, sinceMinutes }),
      });
      if (myGen !== genRef.current) return;     // stale: customer/window changed
      setData((prev) => {
        if (prev) setPrevCounts({ mail: prev.newMail.rowCount, clicks: prev.newClicks.rowCount });
        return res;
      });
    } catch (e) {
      if (myGen !== genRef.current) return;
      if (e instanceof ApiError) {
        setError(e.circuitOpen ? `Service cooling down: ${e.message}` : e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load live watch');
      }
    } finally {
      inFlightRef.current = false;
      if (myGen === genRef.current) setLoading(false);
    }
  }, [customerId, sinceMinutes, noCustomer]);

  // Reset on customer / window change. Bumping genRef invalidates any
  // in-flight poll started against the previous tenant/window so its result
  // never reaches setData.
  useEffect(() => {
    genRef.current++;
    setData(null);
    setPrevCounts(null);
    setLoading(false);
  }, [customerId, sinceMinutes]);

  // Initial fetch + polling loop. Pauses when the tab is hidden — both by not
  // arming the interval and by short-circuiting any in-flight tick.
  useEffect(() => {
    if (noCustomer || !autoRefresh) return;
    let cancelled = false;
    const tick = () => { if (!document.hidden && !cancelled) void poll(); };
    tick();
    const id = window.setInterval(tick, LIVE_WATCH_INTERVAL_MS);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [poll, autoRefresh, noCustomer]);

  const mailRows   = useMemo(() => data ? toObjects(data.newMail.columns,   data.newMail.rows)   : [], [data]);
  const clickRows  = useMemo(() => data ? toObjects(data.newClicks.columns, data.newClicks.rows) : [], [data]);

  // Click events that *reached the page* on a Phish/Malware email — the
  // worst-case "user just clicked a phishing link" signal.
  const dangerousClicks = useMemo(() => clickRows.filter((r) => {
    const at = String(r['ActionType'] ?? '');
    const tt = String(r['EmailThreatTypes'] ?? '').toLowerCase();
    const through = r['IsClickedThrough'] === true || r['IsClickedThrough'] === 'true';
    const allowed = at === 'ClickAllowed' || through;
    return allowed && (tt.includes('phish') || tt.includes('malware'));
  }).length, [clickRows]);

  const inboxLanded = useMemo(() => mailRows.filter((r) => {
    const loc = String(r['LatestDeliveryLocation'] ?? '').toLowerCase();
    const act = String(r['DeliveryAction'] ?? '').toLowerCase();
    return act === 'delivered' && (loc === 'inbox' || loc === 'external' || loc === 'forwarded');
  }).length, [mailRows]);

  const lastRefresh = data ? new Date(data.queriedAt) : null;
  const mailDelta   = data && prevCounts ? data.newMail.rowCount   - prevCounts.mail   : null;
  const clickDelta  = data && prevCounts ? data.newClicks.rowCount - prevCounts.clicks : null;

  return (
    <div className="bg-card rounded-xl shadow-md p-4 border border-border">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${autoRefresh && !noCustomer ? 'bg-green-400 animate-pulse' : 'bg-slate-500'}`} aria-hidden />
            <h3 className="text-sm font-bold text-foreground">Live Watch</h3>
            {dangerousClicks > 0 && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-500/30 text-red-700 dark:text-red-200 border border-red-500/50 animate-pulse">
                {dangerousClicks} risky click{dangerousClicks === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            New phish, malware, and high-confidence spam plus Safe Links clicks in the last {sinceMinutes} minute{sinceMinutes === 1 ? '' : 's'}.
            {lastRefresh && (
              <> Last refresh <span className="text-foreground/80">{lastRefresh.toLocaleTimeString()}</span>{loading ? ' · refreshing…' : ''}.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1.5 text-muted-foreground">
            Window
            <select
              value={sinceMinutes}
              onChange={(e) => setSinceMinutes(Number(e.target.value))}
              className="px-2 py-1 rounded bg-background border border-border text-foreground"
            >
              <option value={5}>5 min</option>
              <option value={15}>15 min</option>
              <option value={60}>1 hour</option>
              <option value={360}>6 hours</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-muted-foreground">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-primary"
            />
            Auto-refresh
          </label>
          <button
            onClick={() => void poll()}
            disabled={loading || noCustomer}
            className="font-semibold px-3 py-1 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {noCustomer && (
        <div className="px-3 py-2 bg-amber-500/20 text-amber-700 dark:text-amber-200 text-xs rounded-lg">
          Select a customer above to start the live watch.
        </div>
      )}
      {error && <div className="px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}

      {data && !noCustomer && (
        <>
          {/* KPI strip: total new mail, inbox-landed, total clicks, dangerous clicks */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <KpiTile
              label="New suspicious mail"
              value={data.newMail.rowCount}
              hint={mailDelta !== null && mailDelta !== 0 ? `${mailDelta > 0 ? '+' : ''}${mailDelta} since last poll` : undefined}
            />
            <KpiTile
              label="Reached inbox"
              value={inboxLanded}
              hint={inboxLanded > 0 ? 'Delivered to user mailboxes' : 'All filtered'}
            />
            <KpiTile
              label="Safe Links clicks"
              value={data.newClicks.rowCount}
              hint={clickDelta !== null && clickDelta !== 0 ? `${clickDelta > 0 ? '+' : ''}${clickDelta} since last poll` : undefined}
            />
            <KpiTile
              label="Risky clicks"
              value={dangerousClicks}
              hint={dangerousClicks > 0 ? 'User reached phish page' : 'None reached page'}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <LiveMailPanel section={data.newMail} rows={mailRows} />
            <LiveClicksPanel section={data.newClicks} rows={clickRows} />
          </div>
        </>
      )}
    </div>
  );
}

/** Right-half of the live watch: newly-arrived suspicious mail. */
function LiveMailPanel({ section, rows }: { section: LiveWatchSection; rows: Record<string, unknown>[] }) {
  if (section.status === 'missing') {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">New suspicious mail</div>
        <div className="text-[11px] text-amber-700 dark:text-amber-200">{section.message ?? 'EmailEvents not deployed.'}</div>
      </div>
    );
  }
  if (section.status === 'error') {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">New suspicious mail</div>
        <div className="text-[11px] text-red-700 dark:text-red-300">{section.error ?? 'Query failed.'}</div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">New suspicious mail</div>
        <div className="text-[11px] text-muted-foreground">No new Phish / Malware / high-spam in the window.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-background/40 overflow-hidden">
      <div className="px-3 py-2 text-xs font-semibold text-foreground border-b border-border flex items-center justify-between">
        <span>New suspicious mail</span>
        <span className="text-[10px] font-normal text-muted-foreground">{rows.length}{section.truncated ? ' (capped)' : ''}</span>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-background/60 sticky top-0">
            <tr className="text-left text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">Time</th>
              <th className="px-2 py-1.5 font-medium">Sender</th>
              <th className="px-2 py-1.5 font-medium">Recipient</th>
              <th className="px-2 py-1.5 font-medium">Threat</th>
              <th className="px-2 py-1.5 font-medium">Action / Loc</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const tt = String(r['ThreatTypes'] ?? '').toLowerCase();
              const loc = String(r['LatestDeliveryLocation'] ?? '').toLowerCase();
              const act = String(r['DeliveryAction'] ?? '').toLowerCase();
              const inbox = act === 'delivered' && (loc === 'inbox' || loc === 'external' || loc === 'forwarded');
              const dangerous = inbox && (tt.includes('phish') || tt.includes('malware'));
              return (
                <tr key={i} className={`border-t border-border/60 ${dangerous ? 'bg-red-500/10' : ''}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-foreground/80" title={fmt(r['TimeGenerated'])}>
                    {new Date(fmt(r['TimeGenerated'])).toLocaleTimeString()}
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[14ch]" title={fmt(r['SenderFromAddress'])}>{fmt(r['SenderFromAddress'])}</td>
                  <td className="px-2 py-1.5 truncate max-w-[14ch]" title={fmt(r['RecipientEmailAddress'])}>{fmt(r['RecipientEmailAddress'])}</td>
                  <td className="px-2 py-1.5 truncate max-w-[12ch]" title={fmt(r['ThreatTypes'])}>{fmt(r['ThreatTypes']) || ' - '}</td>
                  <td className="px-2 py-1.5 truncate max-w-[14ch]">
                    <span className={inbox ? 'text-red-700 dark:text-red-300 font-semibold' : 'text-foreground/80'}>
                      {fmt(r['DeliveryAction'])}{r['LatestDeliveryLocation'] ? ` · ${fmt(r['LatestDeliveryLocation'])}` : ''}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Left-half of the live watch: recent Safe Links clicks. */
function LiveClicksPanel({ section, rows }: { section: LiveWatchSection; rows: Record<string, unknown>[] }) {
  if (section.status === 'missing') {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">Recent URL clicks</div>
        <div className="text-[11px] text-amber-700 dark:text-amber-200">{section.message ?? 'UrlClickEvents not deployed (Safe Links not enabled).'}</div>
      </div>
    );
  }
  if (section.status === 'error') {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">Recent URL clicks</div>
        <div className="text-[11px] text-red-700 dark:text-red-300">{section.error ?? 'Query failed.'}</div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-background/40 p-3">
        <div className="text-xs font-semibold text-foreground mb-1">Recent URL clicks</div>
        <div className="text-[11px] text-muted-foreground">No Safe Links clicks in the window.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-background/40 overflow-hidden">
      <div className="px-3 py-2 text-xs font-semibold text-foreground border-b border-border flex items-center justify-between">
        <span>Recent URL clicks</span>
        <span className="text-[10px] font-normal text-muted-foreground">{rows.length}{section.truncated ? ' (capped)' : ''}</span>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-background/60 sticky top-0">
            <tr className="text-left text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">Time</th>
              <th className="px-2 py-1.5 font-medium">User</th>
              <th className="px-2 py-1.5 font-medium">URL</th>
              <th className="px-2 py-1.5 font-medium">Action</th>
              <th className="px-2 py-1.5 font-medium">Threat</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const at = String(r['ActionType'] ?? '');
              const tt = String(r['EmailThreatTypes'] ?? '').toLowerCase();
              const through = r['IsClickedThrough'] === true || r['IsClickedThrough'] === 'true';
              const allowed = at === 'ClickAllowed' || through;
              const dangerous = allowed && (tt.includes('phish') || tt.includes('malware'));
              const blocked = at.startsWith('ClickBlocked');
              return (
                <tr key={i} className={`border-t border-border/60 ${dangerous ? 'bg-red-500/15' : ''}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-foreground/80" title={fmt(r['TimeGenerated'])}>
                    {new Date(fmt(r['TimeGenerated'])).toLocaleTimeString()}
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[14ch]" title={fmt(r['AccountUpn'])}>{fmt(r['AccountUpn']) || ' - '}</td>
                  <td className="px-2 py-1.5 max-w-[26ch]" title={fmt(r['Url'])}>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-foreground/90 truncate">{hostnameOf(fmt(r['Url'])) || fmt(r['Url'])}</span>
                      {(() => {
                        const raw = fmt(r['Url']);
                        const q = hostnameOf(raw) || raw;
                        if (!q) return null;
                        return (
                          <a
                            href={`https://urlscan.io/search/${encodeURIComponent(q)}#*%20${encodeURIComponent(q)}`}
                            target="_blank" rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0 text-[10px] text-primary hover:underline"
                            title="Look up this URL on urlscan.io"
                          >
                            urlscan ↗
                          </a>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                      dangerous ? 'bg-red-500/30 text-red-700 dark:text-red-200 border-red-500/50'
                        : blocked ? 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/40'
                        : allowed ? 'bg-amber-500/20 text-amber-700 dark:text-amber-200 border-amber-500/40'
                        : 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40'
                    }`}>
                      {at || ' - '}{through ? ' · click-through' : ''}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[12ch]" title={fmt(r['EmailThreatTypes'])}>{fmt(r['EmailThreatTypes']) || ' - '}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * SOC sender-hunt: type a sender email or domain, get back every recipient
 * who received from them in the chosen window with message count, time
 * window, sample subjects, threat indicators, and the latest delivery state.
 *
 * Self-contained: own state, own fetch, own days control. The parent's
 * `defaultDays` only seeds the initial value.
 */
function SenderHuntCard({
  customerId, noCustomer, defaultDays, readOnly = false,
}: {
  customerId: string;
  readOnly?: boolean;
  noCustomer: boolean;
  defaultDays: number;
}) {
  const [senderInput, setSenderInput] = useState('');
  const [days, setDays] = useState(defaultDays);
  const [resp, setResp] = useState<SenderHuntResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sender-level AI investigation — same lifecycle/state shape as the email
  // drawer's runAi(), just pointed at /phishing/sender-ai-investigate.
  const [ai, setAi] = useState<SenderAiResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Reset when the customer flips so we never flash another tenant's results.
  useEffect(() => {
    setResp(null);
    setError(null);
    setAi(null);
    setAiError(null);
  }, [customerId]);

  const run = useCallback(async () => {
    const sender = senderInput.trim();
    if (!sender) { setError('Enter a sender email or domain to hunt.'); return; }
    if (noCustomer) { setError('Select a customer first.'); return; }
    setLoading(true);
    setError(null);
    setResp(null);
    try {
      const r = await apiFetch<SenderHuntResponse>('/phishing/sender-hunt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, sender, days }),
      });
      setResp(r);
    } catch (e) {
      if (e instanceof ApiError) setError(e.circuitOpen ? `Service cooling down: ${e.message}` : e.message);
      else setError(e instanceof Error ? e.message : 'Failed to run sender hunt');
    } finally {
      setLoading(false);
    }
  }, [senderInput, days, customerId, noCustomer]);

  const rows = useMemo(
    () => (resp?.status === 'ok' ? toObjects(resp.columns, resp.rows) : []),
    [resp],
  );

  // Roll up totals for the analyst — one render pass, no extra state.
  const totals = useMemo(() => {
    if (rows.length === 0) return null;
    let messages = 0;
    const subjectSet = new Set<string>();
    let firstSeen: number = Number.POSITIVE_INFINITY;
    let lastSeen: number = 0;
    for (const r of rows) {
      messages += Number(r['Messages'] ?? 0);
      const subjects = Array.isArray(r['SampleSubjects']) ? (r['SampleSubjects'] as unknown[]) : [];
      for (const s of subjects) subjectSet.add(String(s));
      const fs = r['FirstSeen'] ? new Date(String(r['FirstSeen'])).getTime() : NaN;
      const ls = r['LastSeen']  ? new Date(String(r['LastSeen'])).getTime()  : NaN;
      if (Number.isFinite(fs) && fs < firstSeen) firstSeen = fs;
      if (Number.isFinite(ls) && ls > lastSeen)  lastSeen  = ls;
    }
    return {
      messages,
      recipients: rows.length,
      subjects: subjectSet.size,
      firstSeen: Number.isFinite(firstSeen) ? new Date(firstSeen) : null,
      lastSeen:  lastSeen > 0 ? new Date(lastSeen) : null,
    };
  }, [rows]);

  const senderForLink = senderInput.trim();
  const senderDomain = senderForLink.includes('@')
    ? senderForLink.split('@').pop()!.toLowerCase()
    : senderForLink.toLowerCase();

  // IOCs to feed the reputation panel: the sender domain + every distinct
  // sender IP we observed across recipient rows. Cap at 25 to stay under the
  // /ioc/reputation/batch limit. Memo-keyed off the rows so it only changes
  // when a fresh hunt completes.
  const senderIocs = useMemo(() => {
    if (!senderDomain || rows.length === 0) return [];
    const set = new Set<string>();
    set.add(senderDomain);
    for (const r of rows) {
      const ips = Array.isArray(r['SenderIPsSeen']) ? (r['SenderIPsSeen'] as unknown[]) : [];
      for (const ip of ips) {
        const v = String(ip ?? '').trim();
        if (v && IPV4_RE.test(v)) set.add(v);
      }
      const latestIp = String(r['LatestSenderIP'] ?? '').trim();
      if (latestIp && IPV4_RE.test(latestIp)) set.add(latestIp);
    }
    return Array.from(set).slice(0, 25);
  }, [rows, senderDomain]);

  /**
   * Roll-up signals an analyst actually wants to see at a glance:
   *   - byDelivery   — message-weighted distribution across delivery actions
   *                    (Delivered / Junked / Quarantined / Blocked / Replaced).
   *                    This is the headline answer to "did the filter catch
   *                    it?" — surfacing it stops us from putting up an "AI
   *                    investigation" UI that talks about a bypass when
   *                    everything was actually quarantined.
   *   - byThreat     — message-weighted Phish / Malware / Spam tagging from
   *                    Defender's ThreatTypes column.
   *   - senderVariants — every distinct From/MailFrom address observed under
   *                    the apex (only meaningful for domain-scope hunts;
   *                    catches spoofing & address-cycling campaigns).
   *   - latestNmidsByRecipient — most recent NetworkMessageId per recipient,
   *                    used to deep-link straight into the email drawer.
   *
   * All counts are message-weighted (not row-weighted) so a single recipient
   * with 50 quarantined messages outweighs 5 recipients with 1 delivered.
   */
  const campaignSignals = useMemo(() => {
    if (rows.length === 0) return null;
    const byDelivery: Record<string, number> = {};
    const byThreat: Record<string, number> = {};
    const senderVariants = new Set<string>();
    const ipCounts: Record<string, number> = {};
    for (const r of rows) {
      const messages = Number(r['Messages'] ?? 0);
      // DeliveryActionsSeen is a make_set across the whole window; weight every
      // observed action by the recipient's message count. Same for ThreatTypes.
      const deliveries = Array.isArray(r['DeliveryActionsSeen']) ? (r['DeliveryActionsSeen'] as unknown[]) : [];
      for (const d of deliveries) {
        const v = String(d ?? '').trim();
        if (!v) continue;
        byDelivery[v] = (byDelivery[v] ?? 0) + messages;
      }
      // ThreatTypes can be a comma-joined string per row ("Phish, Malware") —
      // split & trim so we don't double-count combined tags.
      const threatsRaw = Array.isArray(r['ThreatTypesSeen']) ? (r['ThreatTypesSeen'] as unknown[]) : [];
      const threatLabels = new Set<string>();
      for (const t of threatsRaw) {
        for (const piece of String(t ?? '').split(/[,;]/)) {
          const v = piece.trim();
          if (v) threatLabels.add(v);
        }
      }
      for (const v of threatLabels) byThreat[v] = (byThreat[v] ?? 0) + messages;
      const variants = Array.isArray(r['DistinctSenders']) ? (r['DistinctSenders'] as unknown[]) : [];
      for (const v of variants) {
        const s = String(v ?? '').trim().toLowerCase();
        if (s) senderVariants.add(s);
      }
      const ips = Array.isArray(r['SenderIPsSeen']) ? (r['SenderIPsSeen'] as unknown[]) : [];
      for (const ip of ips) {
        const v = String(ip ?? '').trim();
        if (v && IPV4_RE.test(v)) ipCounts[v] = (ipCounts[v] ?? 0) + messages;
      }
    }
    return {
      byDelivery,
      byThreat,
      senderVariants: Array.from(senderVariants).sort(),
      ipCounts: Object.entries(ipCounts).sort((a, b) => b[1] - a[1]).slice(0, 10),
    };
  }, [rows]);

  /**
   * Build a CSV from the recipients table the analyst is already looking at,
   * so they can paste it into a ticket / spreadsheet without re-running KQL.
   * We intentionally include only the columns Defender returns in the rollup
   * (no PII enrichment, no rewriting), and we URL-encode via Blob so non-ASCII
   * subjects survive the round trip.
   */
  const exportCsv = useCallback(() => {
    if (rows.length === 0) return;
    const cols = ['Recipient','Messages','Subjects','FirstSeen','LastSeen','LatestDeliveryAction','LatestLocation','LatestThreatTypes','LatestSenderIP','LatestNMID','SampleSubjects'];
    const escape = (v: unknown): string => {
      const s = Array.isArray(v) ? (v as unknown[]).map(String).join(' | ') : String(v ?? '');
      // RFC 4180: wrap in quotes, double internal quotes
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(',')];
    for (const r of rows) lines.push(cols.map((c) => escape(r[c])).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sender-hunt-${senderDomain || 'export'}-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rows, senderDomain]);

  const phishingWritable = !readOnly && !noCustomer;

  // Fire the sender-level AI investigation. We deliberately do NOT block on
  // url-risk having been scanned first — the route handles a missing urlRisk
  // gracefully — but if it has run we pass it through so the model gets the
  // RDAP/NRD verdict in the same prompt.
  const runAi = useCallback(async () => {
    const sender = senderInput.trim();
    if (!sender) { setAiError('Run a sender hunt first.'); return; }
    setAiLoading(true);
    setAiError(null);
    setAi(null);
    try {
      const r = await apiFetch<SenderAiResponse>('/phishing/sender-ai-investigate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, sender, days }),
      });
      setAi(r);
    } catch (e) {
      if (e instanceof ApiError) {
        setAiError(e.circuitOpen ? `AI service cooling down: ${e.message}` : e.message);
      } else {
        setAiError(e instanceof Error ? e.message : 'AI investigation failed');
      }
    } finally {
      setAiLoading(false);
    }
  }, [senderInput, customerId, days]);

  return (
    <div className="bg-card rounded-xl shadow-md p-4 border border-border">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-foreground">Sender Hunt</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Find every user in this tenant who received mail from a specific sender (full email or just a domain).
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading || noCustomer || !senderInput.trim() || readOnly}
          title={readOnly ? "You don't have write permission on the phishing tab for this customer." : undefined}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Hunting…' : 'Run sender hunt'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <Field label="Sender email or domain">
            <input
              value={senderInput}
              onChange={(e) => setSenderInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              placeholder="alice@bad.example or bad.example"
              className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
            />
          </Field>
        </div>
        <Field label="Lookback (days)">
          <input
            type="number" min={1} max={90} value={days}
            onChange={(e) => setDays(Math.min(90, Math.max(1, Number(e.target.value) || 7)))}
            className="w-full text-xs px-2 py-1.5 rounded bg-background border border-border text-foreground"
          />
        </Field>
      </div>

      {error && <div className="mt-3 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}
      {resp?.status === 'missing' && (
        <div className="mt-3 px-3 py-2 bg-amber-500/20 text-amber-700 dark:text-amber-200 text-xs rounded-lg">
          {resp.message ?? 'EmailEvents table is not available for this customer.'}
        </div>
      )}
      {resp?.status === 'error' && (
        <div className="mt-3 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">
          Sender hunt failed{resp.error ? `: ${resp.error}` : ''}.
        </div>
      )}

      {/* Empty result hint — distinct from missing-table so the analyst knows
          the query ran but found nothing. */}
      {resp?.status === 'ok' && rows.length === 0 && (
        <div className="mt-3 px-3 py-2 bg-background/40 border border-border text-xs text-muted-foreground rounded-lg">
          No recipients received mail from <span className="font-mono">{resp.sender}</span> in the last {resp.days} day(s).
        </div>
      )}

      {totals && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <KpiTile label="Messages" value={totals.messages} hint={`Across ${days}d`} />
          <KpiTile label="Recipients" value={totals.recipients} hint="Distinct mailboxes" />
          <KpiTile label="Subjects" value={totals.subjects} hint="Distinct subjects (sampled)" />
          <div className="rounded-md border border-border bg-card/60 p-2 flex flex-col justify-center">
            <div className="text-[10px] text-muted-foreground">Window</div>
            <div className="text-[11px] text-foreground/90 leading-tight">
              {totals.firstSeen ? totals.firstSeen.toLocaleString() : '?'}
            </div>
            <div className="text-[11px] text-foreground/90 leading-tight">
              {totals.lastSeen ? `→ ${totals.lastSeen.toLocaleString()}` : ''}
            </div>
          </div>
        </div>
      )}

      {totals && resp?.truncated && (
        <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
          Result capped at 500 recipients - narrow the time window to see more.
        </div>
      )}

      {/* Quick-action row — pivots the analyst into adjacent tools without
          having to copy/paste the sender. Defender Threat Explorer is the
          M365 surface for the same telemetry we just queried; urlscan.io
          gives passive DNS / page render history; CSV export puts the
          recipients table on the analyst's clipboard for ticketing. */}
      {senderDomain && totals && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <a
            href={`https://security.microsoft.com/threatexplorerv3?viewid=Phish`}
            target="_blank" rel="noreferrer"
            className="text-primary hover:underline"
            title="Open Microsoft Defender Threat Explorer (filter by sender once it loads)"
          >
            Defender Threat Explorer ↗
          </a>
          <a
            href={`https://urlscan.io/search/${encodeURIComponent(senderDomain)}#*%20${encodeURIComponent(senderDomain)}`}
            target="_blank" rel="noreferrer"
            className="text-primary hover:underline"
            title="Open sender domain in urlscan.io"
          >
            urlscan.io: {senderDomain} ↗
          </a>
          <button
            type="button"
            onClick={exportCsv}
            className="text-primary hover:underline"
            title="Download recipients table as CSV"
          >
            Export CSV ↓
          </button>
        </div>
      )}

      {/* ── Campaign signals — the answer to "did the filter catch it?" ──
          Surfaced ABOVE the recipients table because every analyst will
          want to see this first. Pills are colour-graded by analyst
          intuition: red == landed in the inbox, amber == landed in junk
          or got partially handled, green == filter blocked / quarantined. */}
      {campaignSignals && (Object.keys(campaignSignals.byDelivery).length > 0 || Object.keys(campaignSignals.byThreat).length > 0) && (
        <div className="mt-3 rounded-md border border-border bg-card/60 p-3">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Campaign signals
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.keys(campaignSignals.byDelivery).length > 0 && (
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Delivery action (message-weighted)</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(campaignSignals.byDelivery)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <span key={k} className={`text-[11px] px-2 py-0.5 rounded border ${deliveryPillClass(k)}`}>
                        {k}: <span className="font-semibold">{v}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}
            {Object.keys(campaignSignals.byThreat).length > 0 && (
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Threat tagging (message-weighted)</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(campaignSignals.byThreat)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <span key={k} className={`text-[11px] px-2 py-0.5 rounded border ${threatPillClass(k)}`}>
                        {k}: <span className="font-semibold">{v}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Distinct sender variants under the apex — meaningful when the
              hunt was scoped to a domain. Multiple variants is a strong
              signal of address-cycling / display-name spoofing. */}
          {campaignSignals.senderVariants.length > 1 && (
            <details className="mt-3 text-[11px]">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                {campaignSignals.senderVariants.length} distinct sender addresses under this apex (click to expand)
              </summary>
              <ul className="mt-1 ml-4 space-y-0.5 font-mono text-[11px] text-foreground/90 break-all">
                {campaignSignals.senderVariants.slice(0, 50).map((s) => (
                  <li key={s}>{s}</li>
                ))}
                {campaignSignals.senderVariants.length > 50 && (
                  <li className="text-muted-foreground">…and {campaignSignals.senderVariants.length - 50} more</li>
                )}
              </ul>
            </details>
          )}

          {/* Top sender IPs with one-click pivots into AbuseIPDB / urlscan.
              Counts are message-weighted so the IP doing the heavy lifting
              floats to the top regardless of how many recipients it hit. */}
          {campaignSignals.ipCounts.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] text-muted-foreground mb-1">Top sender IPs</div>
              <ul className="space-y-1">
                {campaignSignals.ipCounts.map(([ip, count]) => (
                  <li key={ip} className="text-[11px] flex flex-wrap items-center gap-2">
                    <span className="font-mono text-foreground/90">{ip}</span>
                    <span className="text-muted-foreground">{count} msg</span>
                    <a
                      href={`https://www.abuseipdb.com/check/${encodeURIComponent(ip)}`}
                      target="_blank" rel="noreferrer"
                      className="text-primary hover:underline"
                    >abuseipdb ↗</a>
                    <a
                      href={`https://urlscan.io/ip/${encodeURIComponent(ip)}`}
                      target="_blank" rel="noreferrer"
                      className="text-primary hover:underline"
                    >urlscan ↗</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {rows.length > 0 && <SenderHuntTable rows={rows} />}

      {/* ── Sender investigation surface ──────────────────────────────────
          Mirrors the email-drawer experience for sender-level hunts:
            1. URL Risk Scan on the sender's apex (RDAP, structural,
               PhishTank, WhoisDS NRD)
            2. IOC Reputation panel for the sender domain + observed
               sender IPs (multi-provider lookup)
            3. AI Investigate — sender-tuned Azure OpenAI verdict +
               recommended actions
          Everything reuses the components from the email drawer so the
          analyst sees the same UI patterns regardless of entry point. */}
      {senderDomain && totals && (
        <div className="mt-3">
          <UrlRiskPanel
            urls={[`https://${senderDomain}`]}
            customerId={customerId}
          />
        </div>
      )}

      {senderIocs.length > 0 && (
        <div className="mt-3">
          <ReputationPanel iocs={senderIocs} customerId={customerId} />
        </div>
      )}

      {/* Post-delivery activity — the wow-factor pivot. Once we know who got
          mail from this sender, did anyone CLICK, did anything LAND on disk,
          did any device PHONE HOME to the sender's apex, did any recipient
          show RISKY SIGN-INS after first delivery? Auto-fetches whenever
          the sender hunt produces results so analysts get the answer
          immediately without an extra click. */}
      {senderDomain && totals && (
        <div className="mt-3">
          <SenderPostDeliveryPanel
            customerId={customerId}
            sender={senderInput.trim() || senderDomain}
            days={days}
          />
        </div>
      )}

      {/* AI Investigate — only enabled once we have hunt results, since the
          backend re-runs the same KQL and there's nothing useful to ask the
          model about an unknown sender. */}
      {totals && (
        <div className="mt-3 bg-card rounded-xl shadow-md p-3 border border-border">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-foreground">AI Sender Investigation</h4>
            <button
              onClick={runAi}
              disabled={aiLoading || loading || !phishingWritable}
              title={!phishingWritable ? "You don't have write permission on the phishing tab for this customer." : undefined}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {aiLoading ? 'Investigating…' : ai ? 'Re-run investigation' : 'Investigate sender'}
            </button>
          </div>
          {aiError && (
            <div className="mt-2 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">
              {aiError}
            </div>
          )}
          {ai && (
            <div className="mt-3">
              <p className="text-[10px] text-muted-foreground mb-2">
                Grounded in {ai.grounding?.length ?? 0} live KQL queries against your Log Analytics workspace
                (EmailEvents, EmailUrlInfo, EmailAttachmentInfo, EmailPostDeliveryEvents, UrlClickEvents,
                DeviceFileEvents, DeviceNetworkEvents, AADSignInEventsBeta).
              </p>
              {ai.grounding && ai.grounding.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1">
                  {ai.grounding.map((g) => {
                    const cls =
                      g.status === 'ok'      ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/40' :
                      g.status === 'empty'   ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30' :
                      g.status === 'missing' ? 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30' :
                                               'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
                    const label =
                      g.status === 'ok'      ? `${g.section}: ${g.rowCount}` :
                      g.status === 'empty'   ? `${g.section}: 0` :
                      g.status === 'missing' ? `${g.section}: not deployed` :
                                               `${g.section}: error`;
                    return (
                      <span key={g.section} className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{label}</span>
                    );
                  })}
                </div>
              )}
              <AiAnalysisCard ai={ai} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Per-recipient breakdown rendered as a sortable, scrollable table. */
function SenderHuntTable({ rows }: { rows: Record<string, unknown>[] }) {
  // We keep sort logic local — the parent already truncates at 500.
  type SortKey = 'Recipient' | 'Messages' | 'LastSeen';
  const [sortKey, setSortKey] = useState<SortKey>('Messages');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const cmp = (a: Record<string, unknown>, b: Record<string, unknown>) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (sortKey === 'Messages') return (Number(av ?? 0) - Number(bv ?? 0));
      if (sortKey === 'LastSeen') {
        const at = av ? new Date(String(av)).getTime() : 0;
        const bt = bv ? new Date(String(bv)).getTime() : 0;
        return at - bt;
      }
      return String(av ?? '').localeCompare(String(bv ?? ''));
    };
    const out = [...rows].sort(cmp);
    if (dir === 'desc') out.reverse();
    return out;
  }, [rows, sortKey, dir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setDir(k === 'Recipient' ? 'asc' : 'desc'); }
  };

  const arrow = (k: SortKey) => sortKey === k ? (dir === 'asc' ? ' ▲' : ' ▼') : '';

  // Map our sort key to the proper aria-sort value on the active column so
  // assistive tech can announce the current order.
  const ariaSort = (k: SortKey): 'ascending' | 'descending' | 'none' =>
    k === sortKey ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';

  // Sortable header cell — header is a real <button> for keyboard support
  // (Enter/Space activate it natively), wrapped in a <th> with aria-sort.
  const SortHeader = ({ k, label, align = 'left' }: { k: SortKey; label: string; align?: 'left' | 'right' }) => (
    <th
      scope="col"
      aria-sort={ariaSort(k)}
      className={`px-2 py-1.5 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className="font-medium text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary rounded px-0.5"
      >
        {label}{arrow(k)}
      </button>
    </th>
  );

  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead className="bg-background/60 text-muted-foreground">
          <tr>
            <SortHeader k="Recipient" label="Recipient" />
            <SortHeader k="Messages"  label="Msgs" align="right" />
            <SortHeader k="LastSeen"  label="Last seen" />
            <th scope="col" className="text-left px-2 py-1.5">Latest threat</th>
            <th scope="col" className="text-left px-2 py-1.5">Latest delivery</th>
            <th scope="col" className="text-left px-2 py-1.5">Sample subjects</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const recipient = String(r['Recipient'] ?? '');
            const msgs = Number(r['Messages'] ?? 0);
            const last = r['LastSeen'] ? new Date(String(r['LastSeen'])).toLocaleString() : ' - ';

            // Singular latest values from arg_max — these are guaranteed to
            // be from the same most-recent event for this recipient.
            const latestThreat = String(r['LatestThreatTypes'] ?? '').trim();
            const latestAction = String(r['LatestDeliveryAction'] ?? '').trim();
            const latestLoc    = String(r['LatestLocation'] ?? '').trim();

            // Historical sets, used only as supplementary hint when the
            // recipient has seen multiple distinct values over the window.
            const actionsSet = Array.isArray(r['DeliveryActionsSeen'])
              ? Array.from(new Set((r['DeliveryActionsSeen'] as unknown[]).map(String).filter(Boolean)))
              : [];
            const locsSet = Array.isArray(r['LocationsSeen'])
              ? Array.from(new Set((r['LocationsSeen'] as unknown[]).map(String).filter(Boolean)))
              : [];
            const otherActions = actionsSet.filter((a) => a && a !== latestAction);
            const otherLocs    = locsSet.filter((l) => l && l !== latestLoc);

            const subjects = Array.isArray(r['SampleSubjects'])
              ? (r['SampleSubjects'] as unknown[]).map(String).filter(Boolean)
              : [];

            // Color the row when the **latest** threat indicates Phish/Malware
            // so the analyst's eye lands on the actionable rows first.
            const threaty = /Phish|Malware/i.test(latestThreat);
            return (
              <tr
                key={`${recipient}-${i}`}
                className={`border-t border-border ${threaty ? 'bg-red-500/5' : ''}`}
              >
                <td className="px-2 py-1.5 font-mono text-foreground/90 break-all">{recipient || <span className="text-muted-foreground">(unknown)</span>}</td>
                <td className="px-2 py-1.5 text-right font-semibold">{msgs.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{last}</td>
                <td className="px-2 py-1.5">
                  {latestThreat
                    ? <span className={`text-[10px] px-1.5 py-0.5 rounded border ${threaty ? 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40' : 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40'}`}>{latestThreat}</span>
                    : <span className="text-muted-foreground"> - </span>}
                </td>
                <td className="px-2 py-1.5 text-foreground/80">
                  {!latestAction && !latestLoc
                    ? <span className="text-muted-foreground"> - </span>
                    : (
                      <div className="flex flex-col">
                        {latestAction && <span className="text-[11px]">{latestAction}</span>}
                        {latestLoc && <span className="text-[10px] text-muted-foreground">{latestLoc}</span>}
                        {(otherActions.length > 0 || otherLocs.length > 0) && (
                          <span
                            className="text-[10px] text-amber-700 dark:text-amber-300/80 mt-0.5"
                            title={`Also seen - actions: ${otherActions.join(', ') || 'none'}; locations: ${otherLocs.join(', ') || 'none'}`}
                          >
                            +{otherActions.length + otherLocs.length} other state(s) over window
                          </span>
                        )}
                      </div>
                    )}
                </td>
                <td className="px-2 py-1.5 text-foreground/70">
                  {subjects.length === 0
                    ? <span className="text-muted-foreground"> - </span>
                    : <span className="text-[11px]" title={subjects.join('\n')}>{subjects.slice(0, 2).join(' · ')}{subjects.length > 2 ? ` +${subjects.length - 2}` : ''}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label || '\u00A0'}</div>
      {children}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Delivered-phish table
// ─────────────────────────────────────────────────────────────────────────────

const TABLE_COLUMNS: { key: string; label: string; width?: string }[] = [
  { key: 'TimeGenerated',         label: 'Time' },
  { key: 'RecipientEmailAddress', label: 'Recipient' },
  { key: 'SenderFromAddress',     label: 'Sender' },
  { key: 'Subject',               label: 'Subject' },
  { key: 'ThreatTypes',           label: 'Threat' },
  { key: 'LatestDeliveryLocation',label: 'Location' },
  { key: 'OrgLevelAction',        label: 'Org action' },
];

function DeliveredTable({ response, onOpen }: { response: DeliveredResponse; onOpen: (nmid: string) => void }) {
  const [sortKey, setSortKey] = useState<string>('TimeGenerated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const objs = useMemo(() => toObjects(response.columns, response.rows), [response]);

  const sorted = useMemo(() => {
    const copy = [...objs];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const as = av == null ? '' : String(av);
      const bs = bv == null ? '' : String(bv);
      const cmp = as < bs ? -1 : as > bs ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [objs, sortKey, sortDir]);

  if (sorted.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6 border border-border text-sm text-muted-foreground text-center">
        No bypassed phishing or malware emails in the selected window.
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-background/40 text-muted-foreground">
            <tr>
              {TABLE_COLUMNS.map((c) => {
                const active = sortKey === c.key;
                return (
                  <th
                    key={c.key}
                    onClick={() => {
                      if (active) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                      else { setSortKey(c.key); setSortDir('desc'); }
                    }}
                    className="text-left font-semibold px-3 py-2 cursor-pointer select-none whitespace-nowrap"
                  >
                    {c.label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const nmid = String(row['NetworkMessageId'] ?? '');
              return (
                <tr
                  key={`${nmid}-${i}`}
                  onClick={() => nmid && onOpen(nmid)}
                  className="border-t border-border hover:bg-background/40 cursor-pointer"
                >
                  {TABLE_COLUMNS.map((c) => (
                    <td key={c.key} className="px-3 py-2 align-top text-foreground/90">
                      {c.key === 'TimeGenerated'
                        ? new Date(String(row[c.key])).toLocaleString()
                        : <span className="block max-w-[26ch] truncate" title={fmt(row[c.key])}>{fmt(row[c.key])}</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail drawer + AI investigation
// ─────────────────────────────────────────────────────────────────────────────

function EmailDetailDrawer({
  customerId, networkMessageId, onClose,
}: { customerId: string; networkMessageId: string; onClose: () => void }) {
  const { canWrite } = useAuth();
  const phishingWritable = canWrite('phishing', customerId);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [ai, setAi] = useState<AiResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    setAi(null);
    setAiError(null);
    apiFetch<DetailResponse>('/phishing/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, networkMessageId }),
    })
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load email'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, networkMessageId]);

  async function runAi() {
    setAiLoading(true);
    setAiError(null);
    setAi(null);
    try {
      const r = await apiFetch<AiResponse>('/phishing/ai-investigate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, networkMessageId }),
      });
      setAi(r);
    } catch (e) {
      if (e instanceof ApiError) {
        setAiError(e.circuitOpen ? `AI service cooling down: ${e.message}` : e.message);
      } else {
        setAiError(e instanceof Error ? e.message : 'AI investigation failed');
      }
    } finally {
      setAiLoading(false);
    }
  }

  // Esc-to-close — small UX nicety; the entity panel does the same.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const emailRow = useMemo(() => {
    const sec = detail?.sections.find((s) => s.id === 'email');
    if (!sec || sec.status !== 'ok') return null;
    const objs = toObjects(sec.columns, sec.rows);
    return objs[0] ?? null;
  }, [detail]);

  const recipientsRow = useMemo(() => {
    const sec = detail?.sections.find((s) => s.id === 'recipients');
    if (!sec || sec.status !== 'ok') return null;
    return toObjects(sec.columns, sec.rows)[0] ?? null;
  }, [detail]);

  const campaignRow = useMemo(() => {
    const sec = detail?.sections.find((s) => s.id === 'campaign');
    if (!sec || sec.status !== 'ok') return null;
    return toObjects(sec.columns, sec.rows)[0] ?? null;
  }, [detail]);

  // Auto-extract IOCs from the loaded telemetry: sender IP, attachment SHA256s,
  // URL hostnames, and the sender domain itself. Capped at 25 to stay under
  // the /ioc/reputation/batch limit.
  const extractedIocs = useMemo(() => {
    if (!detail) return [] as string[];
    const set = new Set<string>();
    const add = (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s && s.length <= 1024) set.add(s);
    };
    if (emailRow) {
      add(emailRow['SenderIPv4']);
      const sender = String(emailRow['SenderFromAddress'] ?? '');
      const at = sender.lastIndexOf('@');
      if (at > -1) add(sender.slice(at + 1).toLowerCase());
    }
    for (const sec of detail.sections) {
      if (sec.status !== 'ok') continue;
      const objs = toObjects(sec.columns, sec.rows);
      if (sec.id === 'urls') {
        for (const r of objs) {
          add(r['UrlDomain'] || hostnameOf(String(r['Url'] ?? '')));
        }
      } else if (sec.id === 'attachments') {
        for (const r of objs) add(r['SHA256']);
      } else if (sec.id === 'urlClicks') {
        for (const r of objs) add(hostnameOf(String(r['Url'] ?? '')));
      }
    }
    return Array.from(set).filter(Boolean).slice(0, 25);
  }, [detail, emailRow]);

  // Full URLs (not just hostnames) extracted from the email's urls +
  // urlClicks sections. These feed the layered URL Risk panel which
  // analyses the entire URL (path + query, not just the host).
  const extractedUrls = useMemo(() => {
    if (!detail) return [] as string[];
    const set = new Set<string>();
    for (const sec of detail.sections) {
      if (sec.status !== 'ok') continue;
      const objs = toObjects(sec.columns, sec.rows);
      if (sec.id === 'urls' || sec.id === 'urlClicks') {
        for (const r of objs) {
          const u = String(r['Url'] ?? '').trim();
          if (u && /^https?:\/\//i.test(u) && u.length <= 2048) set.add(u);
        }
      }
    }
    return Array.from(set).slice(0, 15);
  }, [detail]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-3xl h-full bg-card border-l border-border shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">NetworkMessageId</div>
            <div className="font-mono text-xs text-foreground truncate">{networkMessageId}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runAi} disabled={aiLoading || loading || !emailRow || !phishingWritable}
              title={!phishingWritable ? "You don't have write permission on the phishing tab for this customer." : undefined}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50"
            >
              {aiLoading ? 'Investigating…' : 'AI investigate'}
            </button>
            <button
              onClick={onClose}
              className="text-xs px-2 py-1.5 rounded-lg bg-background border border-border text-foreground hover:bg-background/60"
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {loading && <div className="text-xs text-muted-foreground">Loading email telemetry…</div>}
          {error && <div className="px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}

          {emailRow && <EmailFactsCard row={emailRow} />}

          {/* Always render ReachCard so the analyst sees a status banner when
              the recipients/campaign sections are missing or errored, instead
              of silently hiding the whole panel. */}
          {detail && (
            <ReachCard
              recipientsSec={detail.sections.find((s) => s.id === 'recipients') ?? null}
              campaignSec={detail.sections.find((s) => s.id === 'campaign') ?? null}
              recipientsRow={recipientsRow}
              campaignRow={campaignRow}
              senderEmail={String(emailRow?.['SenderFromAddress'] ?? '')}
              recipientEmail={String(emailRow?.['RecipientEmailAddress'] ?? '')}
            />
          )}

          {extractedIocs.length > 0 && (
            <ReputationPanel iocs={extractedIocs} customerId={customerId} />
          )}

          {extractedUrls.length > 0 && (
            <UrlRiskPanel urls={extractedUrls} customerId={customerId} />
          )}

          {ai && <AiAnalysisCard ai={ai} />}
          {aiError && <div className="px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{aiError}</div>}

          {detail?.sections
            .filter((s) => s.id !== 'email' && s.id !== 'recipients' && s.id !== 'campaign')
            .map((sec) => (
              <DetailSectionCard key={sec.id} section={sec} />
            ))}
        </div>
      </div>
    </div>
  );
}

function EmailFactsCard({ row }: { row: Record<string, unknown> }) {
  const fields: { label: string; key: string }[] = [
    { label: 'Time',        key: 'TimeGenerated' },
    { label: 'Recipient',   key: 'RecipientEmailAddress' },
    { label: 'Sender',      key: 'SenderFromAddress' },
    { label: 'Sender IP',   key: 'SenderIPv4' },
    { label: 'Subject',     key: 'Subject' },
    { label: 'Threat',      key: 'ThreatTypes' },
    { label: 'SCL',         key: 'SpamConfidenceLevel' },
    { label: 'Direction',   key: 'EmailDirection' },
    { label: 'DeliveryAction', key: 'DeliveryAction' },
    { label: 'Location',    key: 'LatestDeliveryLocation' },
    { label: 'Org action',  key: 'OrgLevelAction' },
    { label: 'Org policy',  key: 'OrgLevelPolicy' },
    { label: 'User action', key: 'UserLevelAction' },
    { label: 'User policy', key: 'UserLevelPolicy' },
    { label: 'Auth',        key: 'AuthenticationDetails' },
    { label: 'Detection',   key: 'DetectionMethods' },
  ];
  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <h4 className="text-xs font-bold text-foreground mb-2">Email facts</h4>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {fields.map((f) => {
          const v = row[f.key];
          if (v == null || v === '') return null;
          return (
            <div key={f.key} className="flex gap-2">
              <dt className="text-muted-foreground min-w-[8ch]">{f.label}</dt>
              <dd className="text-foreground/90 break-all">{f.key === 'TimeGenerated' ? new Date(String(v)).toLocaleString() : fmt(v)}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

/**
 * Reach card — collapses the recipients fan-out + campaign signal into a few
 * KPI tiles. The interesting numbers for an analyst:
 *   - distinct recipients of the same NetworkMessageId  → blast radius
 *   - distinct messages with the same sender + subject  → campaign size
 *   - sender domain vs. recipient domain                → external?
 */
function ReachCard({
  recipientsSec, campaignSec, recipientsRow, campaignRow, senderEmail, recipientEmail,
}: {
  recipientsSec: DetailSection | null;
  campaignSec: DetailSection | null;
  recipientsRow: Record<string, unknown> | null;
  campaignRow: Record<string, unknown> | null;
  senderEmail: string;
  recipientEmail: string;
}) {
  const senderDomain = senderEmail.includes('@') ? senderEmail.split('@').pop()!.toLowerCase() : '';
  const recipientDomain = recipientEmail.includes('@') ? recipientEmail.split('@').pop()!.toLowerCase() : '';
  const isExternal = Boolean(senderDomain) && Boolean(recipientDomain) && senderDomain !== recipientDomain;
  const haveBothDomains = Boolean(senderDomain) && Boolean(recipientDomain);

  const recipients: number = recipientsRow ? Number(recipientsRow['Recipients'] ?? 0) || 0 : 0;
  const sample: unknown[] = Array.isArray(recipientsRow?.['Sample']) ? (recipientsRow!['Sample'] as unknown[]) : [];
  const campaignMsgs: number = campaignRow ? Number(campaignRow['Messages'] ?? 0) || 0 : 0;
  const campaignRcpts: number = campaignRow ? Number(campaignRow['Recipients'] ?? 0) || 0 : 0;
  const campaignFirst: unknown = campaignRow?.['FirstSeen'];
  const campaignLast: unknown  = campaignRow?.['LastSeen'];

  return (
    <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
      <h4 className="text-xs font-bold text-foreground">Sender &amp; Reach</h4>

      {/* Surface status of the supporting KQL sections so a missing or
          errored table is visible to the analyst instead of silently empty. */}
      {(recipientsSec?.status === 'missing' || campaignSec?.status === 'missing') && (
        <div className="text-[10px] px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200">
          {recipientsSec?.status === 'missing' && campaignSec?.status === 'missing'
            ? 'EmailEvents not deployed in this workspace; recipient and campaign reach unavailable.'
            : recipientsSec?.status === 'missing'
              ? 'Recipient fan-out unavailable (EmailEvents not deployed).'
              : 'Campaign reach unavailable (EmailEvents not deployed).'}
        </div>
      )}
      {(recipientsSec?.status === 'error' || campaignSec?.status === 'error') && (
        <div className="text-[10px] px-2 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200">
          {recipientsSec?.status === 'error' && campaignSec?.status === 'error'
            ? 'Recipient and campaign queries failed.'
            : recipientsSec?.status === 'error'
              ? `Recipient query failed${recipientsSec.error ? ': ' + recipientsSec.error : ''}.`
              : `Campaign query failed${campaignSec?.error ? ': ' + campaignSec.error : ''}.`}
        </div>
      )}

      {/* Sender domain row */}
      {senderDomain && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Sender domain</span>
          <span className="font-mono text-foreground/90 break-all">{senderDomain}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
            isExternal
              ? 'bg-amber-500/20 text-amber-700 dark:text-amber-200 border-amber-500/40'
              : 'bg-green-500/20 text-green-700 dark:text-green-200 border-green-500/40'
          }`}>
            {isExternal ? 'External' : haveBothDomains ? 'Internal' : 'Unknown'}
          </span>
          {senderDomain && (
            <a
              href={`https://urlscan.io/search/${encodeURIComponent(senderDomain)}#*%20${encodeURIComponent(senderDomain)}`}
              target="_blank" rel="noreferrer"
              className="ml-auto text-[10px] text-primary hover:underline"
              title="Open in urlscan.io"
            >
              urlscan.io ↗
            </a>
          )}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <KpiTile label="Recipients (this email)" value={recipients} hint="Same NetworkMessageId" />
        <KpiTile label="Messages (campaign)" value={campaignMsgs} hint="Same sender + subject" />
        <KpiTile label="Recipients (campaign)" value={campaignRcpts} hint="Across all matching messages" />
      </div>

      {/* Sample recipients — useful for the analyst to spot mass-targeting */}
      {sample.length > 0 && (
        <div className="text-xs">
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">Sample recipients ({sample.length})</div>
          <div className="font-mono text-[11px] text-foreground/80 break-all leading-relaxed">
            {sample.slice(0, 25).map((s) => String(s)).join(', ')}
            {sample.length > 25 && <span className="text-muted-foreground"> …</span>}
          </div>
        </div>
      )}

      {/* Campaign window */}
      {campaignMsgs > 0 && Boolean(campaignFirst ?? campaignLast) && (
        <div className="text-[10px] text-muted-foreground">
          Campaign window: {campaignFirst ? new Date(String(campaignFirst)).toLocaleString() : '?'}
          {' → '}
          {campaignLast ? new Date(String(campaignLast)).toLocaleString() : '?'}
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-md border border-border bg-card/60 p-2">
      <div className="text-xl font-bold text-foreground leading-tight">{value.toLocaleString()}</div>
      <div className="text-[10px] text-muted-foreground leading-tight">{label}</div>
      {hint ? <div className="text-[9px] text-muted-foreground/80 mt-0.5">{hint}</div> : null}
    </div>
  );
}

/**
 * ReputationPanel — fires a single batch lookup against the existing
 * /ioc/reputation/batch endpoint for every IOC we extracted from the email
 * (sender IP/domain, attachment SHA256s, URL hostnames). Reuses the same
 * scoring logic as the IocReputationCard but renders a compact table
 * inline in the drawer so the analyst doesn't have to copy/paste each value.
 *
 * The analyst can also one-click into urlscan.io for any domain/URL — that
 * provider is not configured by default but its public scans don't need an
 * API key for browsing.
 */
function ReputationPanel({ iocs, customerId }: { iocs: string[]; customerId: string }) {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<BatchReportEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);

  // Memo-key off the joined list so re-opening the same drawer doesn't refetch.
  const key = useMemo(() => iocs.join('|'), [iocs]);

  const run = useCallback(async () => {
    setLoading(true); setError(null); setReports([]);
    try {
      const r = await apiFetch<{ reports: BatchReportEntry[] }>('/ioc/reputation/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iocs, customerId }),
      });
      setReports(r.reports ?? []);
      setHasRun(true);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.circuitOpen ? `Reputation service cooling down: ${e.message}` : e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Reputation lookup failed');
      }
    } finally {
      setLoading(false);
    }
  }, [iocs]);

  // Reset when the IOC set changes (drawer switched to a different email).
  useEffect(() => { setReports([]); setHasRun(false); setError(null); }, [key]);

  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-foreground">IOC Reputation ({iocs.length})</h4>
        <button
          onClick={run} disabled={loading}
          className="text-[11px] font-semibold px-2.5 py-1 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Looking up…' : hasRun ? 'Re-run' : 'Enrich all'}
        </button>
      </div>

      {error && <div className="px-2 py-1.5 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded mb-2">{error}</div>}

      {!hasRun && !loading && (
        <p className="text-[11px] text-muted-foreground">
          Click <span className="font-semibold">Enrich all</span> to look up these IOCs across configured providers
          (VirusTotal, AbuseIPDB, OTX, Spur). The sender IP, sender domain, attachment hashes and URL hostnames are auto-extracted.
        </p>
      )}

      {hasRun && reports.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-2 py-1">IOC</th>
                <th className="text-left font-semibold px-2 py-1">Type</th>
                <th className="text-left font-semibold px-2 py-1">Verdict</th>
                <th className="text-left font-semibold px-2 py-1">Mal/Susp</th>
                <th className="text-left font-semibold px-2 py-1">Providers</th>
                <th className="text-left font-semibold px-2 py-1">Links</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const verdict: Verdict = r.report?.overallVerdict ?? 'unknown';
                const type = r.report?.type ?? 'unknown';
                const links = (r.report?.results ?? []).filter((x) => x.link);
                const isWebish = type === 'domain' || type === 'url';
                return (
                  <tr key={r.ioc} className="border-t border-border/60">
                    <td className="px-2 py-1 align-top">
                      <span className="font-mono text-foreground/90 break-all block max-w-[36ch]" title={r.ioc}>{r.ioc}</span>
                    </td>
                    <td className="px-2 py-1 align-top text-muted-foreground">{type}</td>
                    <td className="px-2 py-1 align-top">
                      {r.ok ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${verdictPill(verdict)}`}>
                          {verdict}
                        </span>
                      ) : (
                        <span className="text-[10px] text-red-700 dark:text-red-300" title={r.error}>error</span>
                      )}
                    </td>
                    <td className="px-2 py-1 align-top text-foreground/80">
                      {r.report ? `${r.report.malicious}/${r.report.suspicious} of ${r.report.total}` : ' - '}
                    </td>
                    <td className="px-2 py-1 align-top">
                      <div className="flex flex-wrap gap-1">
                        {(r.report?.results ?? []).map((p) => (
                          <span
                            key={p.provider}
                            className={`text-[9px] px-1 py-0.5 rounded border ${verdictPill(p.verdict)}`}
                            title={p.summary || p.error || p.provider}
                          >
                            {p.provider}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-1 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {links.map((p) => (
                          <a
                            key={p.provider}
                            href={p.link} target="_blank" rel="noreferrer"
                            className="text-[10px] text-primary hover:underline"
                          >
                            {p.provider} ↗
                          </a>
                        ))}
                        {isWebish && (
                          <a
                            href={`https://urlscan.io/search/${encodeURIComponent(r.ioc)}#*%20${encodeURIComponent(r.ioc)}`}
                            target="_blank" rel="noreferrer"
                            className="text-[10px] text-primary hover:underline"
                          >
                            urlscan ↗
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasRun && reports.length === 0 && !error && (
        <p className="text-[11px] text-muted-foreground italic">No reputation data returned.</p>
      )}
    </div>
  );
}

/**
 * UrlRiskPanel — runs the layered URL analyser (`/phishing/url-scan`)
 * against every full URL extracted from the email. Verdicts are cached
 * server-side per (customerId, urlHash) for 6h, so re-opening the same
 * email drawer is a no-op DB read. The "Rescan" button per row sets
 * `force: true` to bust the cache when an analyst suspects the verdict
 * is stale.
 */
interface UrlRiskFactor { id: string; label: string; delta: number }
interface UrlRiskReport {
  url: string;
  registeredDomain: string;
  scannedAt: string;
  score: number;
  band: 'safe' | 'suspicious' | 'dangerous';
  factors: UrlRiskFactor[];
  layers: {
    domain: { ok: boolean; ageDays: number | null; registeredAt: string | null; registrar: string | null; knownSafeDomain: boolean; error?: string };
    structural: { hostname: string; typosquatBrand: string | null; suspiciousTld: boolean; isIdn: boolean; hasMixedScript: boolean };
    community: {
      phishtank: { configured: boolean; match: boolean; verifiedPhish: boolean; detailUrl: string | null };
      nrdFeed: { loaded: boolean; feedDate: string | null; match: boolean };
    };
  };
}
interface UrlRiskRow { url: string; loading: boolean; report: UrlRiskReport | null; cached: boolean; error: string | null }

function bandPill(b: UrlRiskReport['band']): string {
  switch (b) {
    case 'dangerous': return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
    case 'suspicious': return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40';
    case 'safe': return 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/40';
  }
}

function UrlRiskPanel({ urls, customerId }: { urls: string[]; customerId: string }) {
  const [rows, setRows] = useState<UrlRiskRow[]>(() => urls.map((url) => ({ url, loading: false, report: null, cached: false, error: null })));
  const [bulkLoading, setBulkLoading] = useState(false);

  // Reset whenever the URL set changes (drawer switched to a different email).
  const key = useMemo(() => urls.join('|'), [urls]);
  useEffect(() => {
    setRows(urls.map((url) => ({ url, loading: false, report: null, cached: false, error: null })));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const scanOne = useCallback(async (url: string, force: boolean) => {
    setRows((rs) => rs.map((r) => (r.url === url ? { ...r, loading: true, error: null } : r)));
    try {
      const r = await apiFetch<{ report: UrlRiskReport; cached: boolean }>('/phishing/url-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, url, force }),
      });
      setRows((rs) => rs.map((row) => (row.url === url ? { url, loading: false, report: r.report, cached: r.cached, error: null } : row)));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'URL scan failed';
      setRows((rs) => rs.map((row) => (row.url === url ? { ...row, loading: false, error: msg } : row)));
    }
  }, [customerId]);

  const scanAll = useCallback(async () => {
    setBulkLoading(true);
    try {
      // Sequential to avoid hammering RDAP / PhishTank in parallel; 15 URLs
      // x ~1s = ~15s worst case, comfortably inside any analyst timeout.
      for (const u of urls) {
        await scanOne(u, false);
      }
    } finally {
      setBulkLoading(false);
    }
  }, [urls, scanOne]);

  const summary = useMemo(() => {
    const scored = rows.filter((r) => r.report);
    const dangerous = scored.filter((r) => r.report?.band === 'dangerous').length;
    const suspicious = scored.filter((r) => r.report?.band === 'suspicious').length;
    return { scored: scored.length, dangerous, suspicious };
  }, [rows]);

  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-xs font-bold text-foreground">URL Risk Scan ({urls.length})</h4>
          {summary.scored > 0 && (
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {summary.dangerous} dangerous · {summary.suspicious} suspicious · {summary.scored - summary.dangerous - summary.suspicious} safe
            </div>
          )}
        </div>
        <button
          onClick={scanAll} disabled={bulkLoading}
          className="text-[11px] font-semibold px-2.5 py-1 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {bulkLoading ? 'Scanning…' : 'Scan all'}
        </button>
      </div>

      <p className="text-[10px] text-muted-foreground mb-2">
        Layered analysis: WHOIS / RDAP registration date and registrar, structural signals (homograph, typosquat,
        suspicious TLD, login keywords), and the free WhoisDS Newly Registered Domains daily feed.
        Verdicts cached for 6h per URL.
      </p>

      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.url} className="rounded border border-border/60 bg-card/50 p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="font-mono text-[11px] text-foreground/90 break-all flex-1 min-w-0" title={row.url}>{row.url}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                {row.report && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${bandPill(row.report.band)}`}>
                    {row.report.band} · {row.report.score}
                  </span>
                )}
                {row.cached && <span className="text-[9px] text-muted-foreground" title="Served from server-side cache">cached</span>}
                <button
                  onClick={() => scanOne(row.url, !!row.report)}
                  disabled={row.loading || bulkLoading}
                  className="text-[10px] font-semibold px-2 py-0.5 rounded border border-border text-foreground hover:bg-background/60 disabled:opacity-50"
                >
                  {row.loading ? '…' : row.report ? 'Rescan' : 'Scan'}
                </button>
              </div>
            </div>

            {row.error && (
              <div className="text-[10px] text-red-700 dark:text-red-300 mt-1">{row.error}</div>
            )}

            {row.report && row.report.factors.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {row.report.factors.map((f) => (
                  <span
                    key={f.id}
                    className={`text-[9px] px-1.5 py-0.5 rounded border ${
                      f.delta > 0
                        ? 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30'
                        : 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/30'
                    }`}
                    title={`${f.delta > 0 ? '+' : ''}${f.delta}`}
                  >
                    {f.label}
                  </span>
                ))}
              </div>
            )}

            {row.report && (
              <div className="mt-1 text-[10px] text-muted-foreground">
                {(() => {
                  const d = row.report.layers.domain;
                  const parts: string[] = [];
                  // Prefer the actual WHOIS / RDAP registration date — that's
                  // what an analyst wants to see and search on directly.
                  if (d.registeredAt) {
                    const iso = d.registeredAt.slice(0, 10);
                    parts.push(d.ageDays !== null ? `registered ${iso} (${d.ageDays}d ago)` : `registered ${iso}`);
                  } else if (d.ageDays !== null) {
                    parts.push(`${d.ageDays}d old`);
                  } else {
                    parts.push('registration date unknown');
                  }
                  if (d.registrar) parts.push(d.registrar);
                  const nrd = row.report.layers.community.nrdFeed;
                  if (nrd) {
                    if (!nrd.loaded) parts.push('WhoisDS NRD: loading');
                    else if (nrd.match) parts.push(`WhoisDS NRD: match (feed ${nrd.feedDate})`);
                    else parts.push('WhoisDS NRD: clean');
                  }
                  return parts.join(' · ');
                })()}
                {row.report.registeredDomain && (
                  <>
                    {' · '}
                    <a
                      href={`https://www.whois.com/whois/${encodeURIComponent(row.report.registeredDomain)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-foreground"
                      title="Open public WHOIS lookup in a new tab"
                    >
                      WHOIS lookup
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailSectionCard({ section }: { section: DetailSection }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="rounded-lg border border-border bg-background/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-foreground"
      >
        <span>{section.title} {section.status === 'ok' ? `(${section.rows.length})` : ''}</span>
        <span className="text-muted-foreground">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="border-t border-border p-3">
          {section.status === 'missing' && (
            <div className="text-xs text-muted-foreground italic">
              Table not available in this workspace.
            </div>
          )}
          {section.status === 'error' && (
            <div className="px-2 py-1.5 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded">{section.error}</div>
          )}
          {section.status === 'ok' && section.rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No rows.</div>
          )}
          {section.status === 'ok' && section.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-muted-foreground">
                  <tr>
                    {section.columns.map((c) => (
                      <th key={c} className="text-left font-semibold px-2 py-1 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((r, i) => (
                    <tr key={i} className="border-t border-border/60">
                      {section.columns.map((_c, j) => (
                        <td key={j} className="px-2 py-1 align-top text-foreground/90">
                          <span className="block max-w-[40ch] truncate" title={fmt(r[j])}>{fmt(r[j])}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Post-delivery activity panel. Fires four KQL queries server-side and
 * surfaces the answer to "did anyone act on this campaign?":
 *   - URL clicks (Safe Links UrlClickEvents)
 *   - Attachment downloads on devices (DeviceFileEvents by SHA256)
 *   - Device → sender-apex network connections (DeviceNetworkEvents)
 *   - Risky sign-ins for recipients AFTER first delivery (AADSignInEventsBeta)
 *
 * Each section is rendered independently. A "missing" status (the
 * underlying table isn't deployed in this customer's workspace — common
 * in tenants without MDE) renders a neutral hint, not an error, so an
 * E5-only customer doesn't see scary red boxes for tables they were
 * never going to have.
 *
 * Auto-fetches on mount and whenever sender/days/customer change. There's
 * no manual "refresh" button on purpose — the panel is informational
 * and the underlying KQL is bounded to 1-90d, so a single fetch per
 * sender hunt is the right cadence.
 */
function SenderPostDeliveryPanel({
  customerId, sender, days,
}: { customerId: string; sender: string; days: number }) {
  type Section = {
    id: 'topSubjects' | 'urls' | 'attachments' | 'postDelivery' | 'clicks' | 'downloads' | 'deviceNetwork' | 'signIns';
    title: string;
    table: string;
    kql: string;
    status: 'ok' | 'missing' | 'error';
    error?: string;
    message?: string;
    columns: string[];
    rows: unknown[][];
    rowCount: number;
  };
  type Resp = { customerId: string; sender: string; days: number; apex: string; queriedAt: string; sections: Section[] };

  const [resp, setResp] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openKql, setOpenKql] = useState<string | null>(null);

  // Re-fetch whenever the input identity changes. We deliberately strip the
  // result on every change so we never flash stale post-delivery data from
  // a previous sender / customer while the new fetch is in flight.
  useEffect(() => {
    let cancelled = false;
    setResp(null);
    setError(null);
    if (!customerId || !sender) return;
    setLoading(true);
    apiFetch<Resp>('/phishing/sender-post-delivery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, sender, days }),
    })
      .then((r) => { if (!cancelled) setResp(r); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) setError(e.circuitOpen ? `Service cooling down: ${e.message}` : e.message);
        else setError(e instanceof Error ? e.message : 'Failed to load post-delivery activity');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, sender, days]);

  // Per-section visual treatment — pick a colour that matches incident
  // severity so the analyst's eye lands on the worst news first.
  function tileTone(s: Section): { dot: string; chip: string } {
    if (s.status !== 'ok' || s.rowCount === 0) return { dot: 'bg-muted-foreground/40', chip: 'text-muted-foreground' };
    // Allowed clicks / risky sign-ins / device hits are all bad. Downloads
    // are bad if Defender ActionType !== "FileQuarantined" but we don't
    // have that nuance at the tile level — treat any row as red.
    return { dot: 'bg-red-500', chip: 'text-red-700 dark:text-red-300' };
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-3 border border-border">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-foreground">Post-delivery activity</h4>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Did anyone click, download, connect, or get popped after this sender's mail landed?
          </p>
        </div>
        {loading && <span className="text-[11px] text-muted-foreground">Querying…</span>}
      </div>

      {error && (
        <div className="mt-2 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">
          {error}
        </div>
      )}

      {resp && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {resp.sections.map((s) => {
            const tone = tileTone(s);
            const headlineCount =
              s.status === 'ok' ? s.rowCount :
              s.status === 'missing' ? '—' : '!';
            return (
              <details key={s.id} className="rounded-md border border-border bg-background/40 p-2 [&[open]>summary]:mb-2">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
                    <span className="text-[12px] font-semibold text-foreground">{s.title}</span>
                  </span>
                  <span className={`text-[11px] font-mono ${tone.chip}`}>
                    {headlineCount}{typeof headlineCount === 'number' ? ' rows' : ''}
                  </span>
                </summary>

                {/* Status messaging — keep "missing" softer than "error". */}
                {s.status === 'missing' && (
                  <div className="text-[11px] text-muted-foreground italic">
                    {s.message ?? `${s.table} not deployed in this workspace.`}
                  </div>
                )}
                {s.status === 'error' && (
                  <div className="text-[11px] text-red-700 dark:text-red-300">
                    Query failed: {s.error ?? 'unknown error'}
                  </div>
                )}

                {/* Table rendering — first 10 rows inline, with a per-section
                    "show KQL" toggle for analysts who want to copy the query
                    into Sentinel themselves. */}
                {s.status === 'ok' && s.rows.length > 0 && (
                  <PostDeliveryRows section={s} />
                )}
                {s.status === 'ok' && s.rows.length === 0 && (
                  <div className="text-[11px] text-muted-foreground italic">
                    No matching events in the last {resp.days}d. Filter is doing its job.
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setOpenKql(openKql === s.id ? null : s.id)}
                  className="mt-2 text-[10px] text-primary hover:underline"
                >
                  {openKql === s.id ? 'Hide KQL' : 'Show KQL'}
                </button>
                {openKql === s.id && (
                  <pre className="mt-1 p-2 bg-muted/30 rounded text-[10px] font-mono whitespace-pre-wrap break-all text-foreground/80">
                    {s.kql}
                  </pre>
                )}
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Compact, read-only renderer for a post-delivery section's rows. We keep
 *  the formatting intentionally dumb — the value here is showing the
 *  analyst what their tenant did, not building yet another table grid. */
function PostDeliveryRows({ section }: { section: { columns: string[]; rows: unknown[][] } }) {
  const max = 10;
  const shown = section.rows.slice(0, max);
  const overflow = section.rows.length - shown.length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            {section.columns.map((c) => (
              <th key={c} className="font-medium px-1 py-0.5 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className="border-t border-border/40 align-top">
              {section.columns.map((c, j) => (
                <td key={c} className="px-1 py-0.5 font-mono break-all max-w-[280px]">
                  {formatPostDeliveryCell(r[j])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {overflow > 0 && (
        <div className="mt-1 text-[10px] text-muted-foreground">
          …and {overflow} more rows (open the KQL in Sentinel for the full set)
        </div>
      )}
    </div>
  );
}

/** Render array values as " | "-joined strings, dates as locale strings,
 *  everything else as String(). Keep it short — table cells are narrow. */
function formatPostDeliveryCell(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((x) => String(x ?? '')).filter(Boolean).join(' | ');
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(v);
}

function AiAnalysisCard({ ai }: { ai: AiResponse | SenderAiResponse }) {
  const a = ai.analysis;
  // Email vs sender investigations differ on more than the footer — the
  // headline field "bypassReason" only makes sense per-email (Defender
  // already filtered, so the question is "why did it slip through"). For a
  // sender campaign, the same JSON slot carries the SENDER VERDICT
  // ("Likely legitimate", "Active phishing", etc.) and the headline label
  // changes accordingly. Same for the footer summary.
  const isSender = !('networkMessageId' in ai);
  const headlineLabel = isSender ? 'Sender verdict' : 'Why it bypassed';
  const footer = isSender
    ? `Based on ${ai.factsSummary.recipients} recipients · ${ai.factsSummary.messages} messages · ${ai.factsSummary.distinctSubjects} distinct subjects · ${ai.factsSummary.senderIPs} sender IPs`
    : `Based on ${ai.factsSummary.urls} URLs · ${ai.factsSummary.attachments} attachments · ${ai.factsSummary.postDeliveryEvents} ZAP events · ${ai.factsSummary.urlClicks} clicks`;
  return (
    <div className="rounded-lg border border-purple-500/40 bg-purple-500/10 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-foreground">AI Investigation</h4>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${SEVERITY_COLOR[a.severity]}`}>
          {a.severity}
        </span>
      </div>

      <div className="text-xs text-foreground/90">
        <div className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wide">{headlineLabel}</div>
        <div className="mt-0.5">{a.bypassReason}</div>
      </div>

      <div className="text-xs text-foreground/90">
        <div className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wide">Attacker objective</div>
        <div className="mt-0.5">{a.attackerObjective}</div>
      </div>

      {a.iocs.length > 0 && (
        <div className="text-xs">
          <div className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wide mb-1">IOCs</div>
          <ul className="space-y-1">
            {a.iocs.map((i, idx) => (
              <li key={idx} className="font-mono text-[11px] text-foreground/90 break-all">
                <span className="text-purple-300">[{i.type}]</span> {i.value}
                {i.note && <span className="text-muted-foreground"> · {i.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {a.recommendedActions.length > 0 && (
        <div className="text-xs">
          <div className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wide mb-1">Recommended actions</div>
          <ol className="list-decimal list-inside space-y-0.5 text-foreground/90">
            {a.recommendedActions.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}

      {a.filterTuning.length > 0 && (
        <div className="text-xs">
          <div className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wide mb-1">Filter tuning</div>
          <ul className="list-disc list-inside space-y-0.5 text-foreground/90">
            {a.filterTuning.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {a.narrative && (
        <details className="text-xs">
          <summary className="cursor-pointer font-semibold text-muted-foreground text-[10px] uppercase tracking-wide">Model narrative</summary>
          <p className="mt-1 whitespace-pre-wrap text-foreground/80">{a.narrative}</p>
        </details>
      )}

      <div className="text-[10px] text-muted-foreground pt-1 border-t border-purple-500/30">
        {footer}
      </div>
    </div>
  );
}
